<template>
  <page-container :title="title">
    <v-row class="ma-0 mb-2 pa-2 tab-header" align-content="center">
      <v-tabs height="36">
        <v-tab :to="{ name: 'project-about', params: { id: $route.params.id } }"
          >About</v-tab
        >
        <v-tab :to="{ name: 'project-jobs', params: { id: $route.params.id } }"
          >Related tasks</v-tab
        >
        <v-spacer></v-spacer>
        <template v-if="$route.name === 'project-about'">
          <feature name="projects" variant="write" data="projects">
            <v-btn
              tile
              v-show="projectInfoChanged || isNew"
              class="button background-color--inactive color--foreground"
              @click="goBack()"
              :disabled="saving"
              >Cancel</v-btn
            >
            <v-btn
              tile
              color="primary"
              class="mx-2 button"
              v-show="projectInfoChanged"
              :disabled="!formValid || saving"
              @click="saveEditedProjectInfo"
              :loading="saving"
              >Save</v-btn
            >
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  class="button btn-cancel"
                  @click="deleteProject"
                  :disabled="saving"
                  v-if="!isNew"
                  v-on="on"
                  v-bind="attrs"
                  ><v-icon>{{ archiveIcon }}</v-icon></v-btn
                >
              </template>
              <span>{{
                this.projectInfo.archived ? 'Unarchive' : 'Archive'
              }}</span>
            </v-tooltip>
          </feature>
        </template>
        <template v-else>
          <search-field></search-field>
          <feature name="jobs" variant="write" data="jobs">
            <template v-if="bulkEditing">
              <v-btn
                class="button background-color--inactive color--foreground"
                tile
                elevation="0"
                @click="bulkEditing = false"
                key="cancel-btn"
                >Cancel</v-btn
              >
              <v-btn
                class="button mx-2"
                tile
                color="primary"
                elevation="0"
                @click="bulkUpdatePopupVisible = true"
                key="action-btn"
                >Bulk update</v-btn
              >
              <v-btn icon @click="bulkDeleteAction"
                ><v-icon>mdi-delete</v-icon></v-btn
              >
            </template>
            <v-btn
              v-else
              color="primary"
              class="text-none button"
              tile
              :to="{
                name: 'job-details',
                params: { id: 'new', projectId: $route.params.id },
              }"
              >New task</v-btn
            >
          </feature>
        </template>
      </v-tabs>
    </v-row>
    <router-view
      @valid="setFormValidity"
      @invalid="setFormValidity"
      :project-info="projectInfo"
      :editedProjectInfo.sync="editedProjectInfo"
      :bulkUpdatePopupVisible.sync="bulkUpdatePopupVisible"
      :bulkEditing.sync="bulkEditing"
      ref="projectContentView"
    ></router-view>
  </page-container>
</template>

<script>
import PageContainer from '@/components/PageContainer.vue'
import SearchField from '@/components/SearchField.vue'
import { db, serverTimestamp } from '@/firebaseConfig'
import { mapActions, mapGetters } from 'vuex'
import { mdiPackageDown, mdiPackageUp } from '@mdi/js'
import { objects } from '@/helpers'

export default {
  props: ['id'],
  components: {
    PageContainer,
    SearchField,
  },
  data() {
    return {
      title: [
        {
          to: 'projects',
          text: 'Projects',
        },
        {
          text: this.id === 'new' ? 'New project' : 'View project',
        },
      ],
      saving: false,
      loading: false,
      formValid: false,
      projectInfo: { archived: false, owners: [] },
      editedProjectInfo: { archived: false, owners: [] },
      bulkUpdatePopupVisible: false,
      bulkEditing: false,
    }
  },
  created() {
    if (!this.isNew) {
      this.getInfo()
    } else {
      this.projectInfo.owners = [this.currentUserWorkforceInfo]
      this.editedProjectInfo.owners = [this.currentUserWorkforceInfo]
    }
  },
  beforeDestroy() {
    if (this.unsubscribe) {
      this.unsubscribe()
    }
  },
  computed: {
    ...mapGetters(['companyId', 'currentUserWorkforceInfo']),
    archiveIcon() {
      if (this.projectInfo.archived === true) {
        return mdiPackageUp
      }
      return mdiPackageDown
    },
    isNew() {
      return this.id === 'new'
    },
    projectInfoChanged() {
      return (
        JSON.stringify(this.projectInfo) !==
        JSON.stringify(this.editedProjectInfo)
      )
    },
    collectionRef() {
      return db.collection(`companies/${this.companyId}/projects`)
    },
    employeesCollectionRef() {
      return db.collection(`companies/${this.companyId}/workforce`)
    },
  },
  methods: {
    ...mapActions(['showError', 'showDeletionSuccess']),
    bulkDeleteAction() {
      this.$refs.projectContentView.$refs.tasksList.bulkDeleteAction()
    },
    goBack() {
      this.$router.go(-1)
    },
    setFormValidity(formValid) {
      this.formValid = formValid
    },
    async getInfo() {
      this.loading = true
      const ref = this.collectionRef.doc(this.id)
      if (this.unsubscribe) {
        this.unsubscribe()
      }

      this.unsubscribe = ref.onSnapshot(
        (doc) => {
          this.projectInfo = Object.assign(
            { status: 'To do', owners: [] },
            doc.data()
          )
          this.editedProjectInfo = Object.assign(
            { status: 'To do', owners: [] },
            this.projectInfo
          )
          this.loading = false
        },
        (error) => {
          console.error(error)
          this.loading = false
        }
      )
    },
    async saveEditedProjectInfo() {
      this.saving = true
      try {
        // cleanup owners
        let cleanOwners = []
        for (let own of this.editedProjectInfo.owners) {
          const pickedProps = (({ firstName, lastName, id, email }) => ({
            firstName,
            lastName,
            id,
            email,
          }))(own)
          cleanOwners.push(pickedProps)
        }

        this.editedProjectInfo.owners = cleanOwners

        if (this.isNew) {
          await this.collectionRef.add(
            objects.withCreatedDataInfo(this.editedProjectInfo)
          )
          this.$router.replace({ name: 'projects' })
        } else {
          await this.collectionRef
            .doc(this.id)
            .update(objects.withUpdatedDataInfo(this.editedProjectInfo))
          this.goBack()
        }
      } catch (error) {
        console.error(error)
        this.showError(error)
      } finally {
        this.saving = false
      }
    },
    async deleteProject() {
      const actionName = this.projectInfo.archived ? 'unarchive' : 'archive'
      const result = await this.$confirm(
        `Are you sure you want to ${actionName} ${this.projectInfo.name}?`,
        { title: 'Please confirm', confirmBtnText: 'Confirm' }
      )

      if (result === true) {
        // Archive project
        try {
          await this.collectionRef.doc(this.id).update(
            objects.withUpdatedDataInfo({
              archived: !this.projectInfo.archived,
              updatedAt: serverTimestamp,
            })
          )
          this.$router.replace({ name: 'projects' })
          this.showDeletionSuccess(`Project has been ${actionName}d.`)
        } catch (error) {
          this.showError(
            error.message ?? 'Failed to archive, please try again later'
          )
        }
      }
    },
  },
}
</script>
